import { Modal, Spin, Form, Select, Button } from "antd";
import { useState } from "react";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import styles from "./Profile.module.scss";
import { theme } from "../../theme";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import CustomForm from "../../CustomComponents/CustomForm";
import CustomModalFooter from "../../Containers/CustomModalFooter";
import {
	getAllocatedSupervisors,
	getAvailableSupervisors,
} from "../../utils/reactQueriesConstants";
import { getErrorFromResponse } from "../../utils/responseUtils";
import { ErrorCodes } from "../../Api";
import { getAvailablePracticeSupervisors } from "../../Requests/academic-user-requests";
import { allocateCompanyPracticeSupervisor } from "../../Requests/company-requests";

const { Option } = Select;

const AddPracticeSupervisorModal = (props: { companyId: string }) => {
	const { t } = useTranslation();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [spinning, setSpinning] = useState(false);
	const [form] = Form.useForm<{
		name: string;
	}>();

	const openGetErrorNotification = (_error: any) => {
		openNotification(
			t("internships.error"),
			t("internships.fetchDataError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const { data: availableSupervisors } = useQuery(
		[getAvailableSupervisors, props.companyId],
		() => getAvailablePracticeSupervisors(props.companyId),
		{
			onError: (err) => {
				openGetErrorNotification(err);
			},
		}
	);

	const queryClient = useQueryClient();
	const invalidateUsersQuery = async () => {
		await queryClient.invalidateQueries(getAvailableSupervisors);
		await queryClient.invalidateQueries(getAllocatedSupervisors);
	};

	const handleShow = () => {
		setIsModalVisible(true);
	};

	const handleClose = () => {
		setIsModalVisible(false);
	};

	const saveChangesError = async (ex: any) => {
		const error = await getErrorFromResponse(ex);

		openNotification(
			t("account.supervisorAllocation"),
			error?.code === ErrorCodes.Forbidden
				? t("account.noPermissions")
				: error?.code === ErrorCodes.CompanyNotFound
				? t("account.companyNotFound")
				: error?.code === ErrorCodes.SupervisorNotFound
				? t("account.supervisorNotFound")
				: error?.code === ErrorCodes.SupervisorAlreadyAllocated
				? t("account.supervisorAlreadyAllocated")
				: t("account.supervisorAllocationError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const missingFieldsError = () => {
		openNotification(
			t("account.saveError"),
			t("usersText.requiredFieldsError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const handleAdd = () => {
		const state = form.getFieldsValue();

		if (!state.name) {
			missingFieldsError();
			return;
		}

		setSpinning(true);

		allocateCompanyPracticeSupervisor(props.companyId, state.name)
			.then(async () => {
				form.setFieldsValue({
					name: "",
				});
				await invalidateUsersQuery();
				openNotification(
					t("account.supervisorAllocation"),
					t("account.supervisorAllocationSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch(saveChangesError)
			.finally(() => {
				setSpinning(false);
				setIsModalVisible(false);
			});
	};

	return (
		<div style={{ display: "flex", justifyContent: "flex-end" }}>
			<Button
				type="primary"
				onClick={handleShow}
				title={t("account.allocateNewSupervisor")}
			>
				{t("account.allocateSupervisor")}
			</Button>
			<Modal
				open={isModalVisible}
				onOk={() => form.submit()}
				onCancel={handleClose}
				title={t("account.allocateSupervisor")}
				width={window.innerWidth > 1215 ? "30%" : "80%"}
				footer={
					<CustomModalFooter
						handleClose={handleClose}
						spinning={spinning}
						handleSave={() => form.submit()}
						confirmButtonName={t("account.confirm")}
					/>
				}
			>
				<Spin spinning={spinning}>
					<Form.Provider onFormFinish={handleAdd}>
						<CustomForm layout={theme.layout} form={form}>
							<Form.Item
								required
								label={t("account.addSupervisorForm.supervisorName") + ":"}
								name="name"
								style={{ marginTop: "-10px", marginBottom: "10px" }}
								rules={[
									{
										required: true,
										message: t("usersText.requiredField", {
											field: t("account.addSupervisorForm.supervisorName"),
										}),
									},
								]}
							>
								<Select
									className={styles.antSelect}
									allowClear
									style={{ width: "400px", textAlign: "left" }}
									placeholder={t("account.addSupervisorForm.supervisorName")}
								>
									{availableSupervisors?.length &&
										availableSupervisors?.length > 0 &&
										availableSupervisors?.map((user) => (
											<Option key={user.id} value={user.id}>
												{user.name}
											</Option>
										))}
								</Select>
							</Form.Item>
						</CustomForm>
					</Form.Provider>
				</Spin>
			</Modal>
		</div>
	);
};

export default AddPracticeSupervisorModal;
